/**
 * ResponseDetailView (RDV) Component
 *
 * Full-screen modal showing complete model response with metadata.
 * Accessed via expander (⤢) button in LensComparisonPanel.
 * Close icon + ESC handler dismiss the modal and restore the prior heatmap state.
 *
 * Part of Pass 4: Expander → RDV
 */

import React, { useEffect } from 'react';
import { getModelDisplayInfo } from '../../../utils/modelDisplayName';
import { getModelLogo } from '../../../utils/logoMapping';

export interface ResponseDetailViewProps {
  modelKey: string;
  response: string;
  criterion?: string;
  criterionLabel?: string;
  score?: number;
  state?: 'agree' | 'mixed' | 'differ';
  metadata?: {
    tokens?: { input: number; output: number; total: number };
    cost?: number;
    responseTime?: number;
  };
  onClose: () => void;
}

export const ResponseDetailView: React.FC<ResponseDetailViewProps> = ({
  modelKey,
  response,
  criterion,
  criterionLabel,
  score,
  state,
  metadata,
  onClose
}) => {
  const modelId = modelKey.split(':')[1];
  const providerId = modelKey.split(':')[0];
  const displayInfo = getModelDisplayInfo(modelId);
  const logo = getModelLogo(modelId);

  // ESC key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Prevent body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const stateColors = {
    agree: 'text-green-400 bg-green-500/20',
    mixed: 'text-yellow-400 bg-yellow-500/20',
    differ: 'text-red-400 bg-red-500/20'
  };

  const stateSymbols = {
    agree: '✓',
    mixed: '≈',
    differ: '✕'
  };

  const navHeight = 'var(--main-nav-height, 4.5rem)';
  const modalHeight = `calc(100vh - ${navHeight})`;

  return (
    <div
      className="fixed inset-x-0 z-50 flex items-start justify-center bg-slate-900/80 backdrop-blur-sm px-2 sm:px-4"
      style={{ top: navHeight, height: modalHeight }}
      data-testid="rdv-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rdv-title"
      onClick={onClose} // Click backdrop to close
    >
      <div
        className="relative w-full max-w-4xl mx-2 sm:mx-4 bg-slate-800/95 rounded-xl border border-slate-700 shadow-2xl overflow-hidden flex flex-col h-full"
        onClick={(e) => e.stopPropagation()} // Prevent backdrop click from closing
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-800 border-b border-slate-700 px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-3">
                {logo && <img src={logo} alt="" className="w-6 h-6 object-contain" />}
                <div>
                  <h2 id="rdv-title" className="text-lg font-semibold text-white">
                    {displayInfo.displayName}
                  </h2>
                  <p className="text-xs text-slate-400">{displayInfo.providerName}</p>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white text-xl min-w-[44px] min-h-[44px] flex items-center justify-center"
              title="Close (ESC)"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Metadata Bar */}
        {(criterion || metadata) && (
          <div className="bg-slate-800/50 border-b border-slate-700 px-3 sm:px-6 py-3">
            <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-sm">
              {criterion && criterionLabel && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Criterion:</span>
                  <span className="text-white font-medium">{criterionLabel}</span>
                  {state && score !== undefined && (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${stateColors[state]}`}>
                      {stateSymbols[state]} {state} ({score}%)
                    </span>
                  )}
                </div>
              )}
              {metadata?.tokens && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">📊 Tokens:</span>
                  <span className="text-white">{metadata.tokens.total.toLocaleString()}</span>
                  <span className="text-slate-500 text-xs">
                    ({metadata.tokens.input} in / {metadata.tokens.output} out)
                  </span>
                </div>
              )}
              {metadata?.cost !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">💰 Cost:</span>
                  <span className="text-white">${metadata.cost.toFixed(4)}</span>
                </div>
              )}
              {metadata?.responseTime !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">⏱️ Time:</span>
                  <span className="text-white">{(metadata.responseTime / 1000).toFixed(2)}s</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Response Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 sm:py-6">
          <div className="prose prose-invert max-w-none">
            <div className="text-sm text-slate-400 uppercase tracking-wide mb-3">
              Full Response:
            </div>
            <pre className="whitespace-pre-wrap text-slate-200 text-sm leading-relaxed font-sans">
              {response}
            </pre>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-slate-800 border-t border-slate-700 px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(response);
                }}
                className="px-3 py-2 min-h-[44px] flex-1 sm:flex-none rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm transition-colors"
                title="Copy response to clipboard"
              >
                📋 Copy
              </button>
              {/* Future: Add share/download buttons */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
